/* House of Coda — the product page controller.

   ONE JOB: turn a size, a grade and a fabric into a variant.

   The catalogue model this is built for:

     Option 1 (the cover) carries two kinds of value. A fabric we have
     actually photographed on this frame is its own value — "Nevis Linen" —
     and buying it selects a married SKU with its own photograph. Every other
     fabric is bought through a generic value — "Grade B" — and the fabric
     itself rides along as a line item property.

     Option 2 is the size.

     Grade is a third role, for the products where it is its own option
     instead of being folded into the cover (see grade_option in the
     section). It behaves exactly like size — a real button per value that
     narrows which variant is selected — and it also announces itself as a
     hoc:grade event on every change, real or resolved (see below), for
     anything else on the page that wants to know the current grade.
     Because the variant JSON only ever carries three slots (o1, o2, o3),
     the section tells this script which one grade actually lives in via
     data-hoc-pd-grade-key.

     Quickship Fabric is a fourth role, for the products that sell a small,
     fixed set of in-stock fabrics as a real option of their own rather than
     through the fabric drawer's open-ended library — see quickship_option
     in the section. It behaves exactly like size and grade: a real button
     per value that narrows which variant is selected, reading and writing
     whichever of the three variant-JSON slots (o1/o2/o3) the section says it
     lives in via data-hoc-pd-quickship-key. It carries no hoc:* announcement
     of its own — nothing outside this script currently needs to know a
     quickship fabric changed — but it accepts a hoc:quickship:request the
     same way grade does, so anything added later can drive it through the
     same single path a real click would. Choosing one is also mutually
     exclusive with a fabric chosen in the drawer, the same way the reverse
     already is: a quickship pick clears a stale drawer fabric (the closure
     variable and the drawer's own [data-hoc-fabric-*] hooks this page
     shows) the same way picking a drawer fabric already clears a stale
     quickship selection.

     Something outside this script can still drive a grade change directly —
     by dispatching hoc:grade:request on document with { grade } — and this
     script is the only thing that ever touches the grade/variant state, so
     that request runs through the exact same path a real button click does
     (state, aria-pressed, sync, the hoc:grade announcement). Nothing in
     this theme dispatches it today: the fabric drawer's own grade row is
     now an independent multi-select filter over the drawer's grid, not a
     control synced to this picker, so it no longer sends this request. The
     path stays here for anything else that wants to change the real grade
     the way a click would.

   So a pick from the drawer resolves in that order: first, whether its name
   is actually one of this product's own Quickship Fabric values (the drawer
   and the quickship list can genuinely overlap — the same fabric, "Raisin",
   can be both a real photographed option and a card in the open-ended
   library) — if so, the pick is redirected into a real quickship click
   below, so the real variant and its own photograph swap in rather than
   just the small material inset. Otherwise: a married variant, then the
   fabric's grade, and if the catalogue has neither yet, the current variant
   stays put and the fabric is still recorded on the order. That last case is
   not a bug — it is how this page behaves correctly today, before the
   products are restructured. Because the drawer's grade filter is
   independent, a picked fabric can resolve to a different grade than this
   page currently shows pressed — the hoc:fabric handler below notices that
   and brings this picker's own state in line with it, the same pressed-state
   update a real click makes, without asking itself for the change via
   hoc:grade:request (that would just be this page signaling itself).

   The drawer itself does not know any of this. It announces hoc:fabric and
   fills the [data-hoc-fabric-*] hooks; everything below is this page reading
   that announcement.

   MOBILE STICKY ADD BAR (Oct 2026)
   ---------------------------------
   Below 1000px .hoc-pd__inner gives up its sticky position (see
   assets/hoc-product.css), so a shopper scrolled down to the fabric/size
   pickers has no way to see the hero photo update without scrolling all the
   way back up. .hoc-pd__stickybar (sections/hoc-product.liquid) exists for
   exactly that: it mirrors the current photo, price and add-to-cart state,
   and this file is what keeps those mirrors in sync and decides when the
   bar is visible.

   Mirroring piggybacks on the two functions that already own this state —
   paint() already sets the one real photo, sync() already sets the one real
   price and the one real add-to-cart label/disabled state — so the sticky
   bar's own elements are updated right alongside them rather than through a
   second, parallel source of truth. Its own add-to-cart button never submits
   anything itself; it only proxies a click to the real [data-hoc-pd-atc]
   button, which stays the single place sold-out/needs-fabric state and the
   actual form submission are decided — the same single-path pattern this
   file already uses for size, grade and quickship. */
(function () {
  'use strict';

  function initProduct(root) {
    root.querySelectorAll('[data-hoc-pd]').forEach(function (pd) {
      if (pd.dataset.hocReady) return;
      pd.dataset.hocReady = '1';

      var store = pd.querySelector('[data-hoc-pd-variants]');
      var variants = [];
      try { variants = JSON.parse(store.textContent || '[]'); } catch (e) { variants = []; }

      var idInput = pd.querySelector('[data-hoc-pd-id]');
      var priceEl = pd.querySelector('[data-hoc-pd-price]');
      var stage = pd.querySelector('[data-hoc-pd-stage]');
      var atc = pd.querySelector('[data-hoc-pd-atc]');
      var sizeBtns = Array.prototype.slice.call(pd.querySelectorAll('[data-hoc-pd-size]'));
      var gradeBtns = Array.prototype.slice.call(pd.querySelectorAll('[data-hoc-pd-grade]'));
      var quickshipBtns = Array.prototype.slice.call(pd.querySelectorAll('[data-hoc-pd-quickship]'));
      var thumbs = Array.prototype.slice.call(pd.querySelectorAll('[data-hoc-pd-thumb]'));
      var figno = pd.querySelector('[data-hoc-pd-figno]');
      /* a fabric is required before adding to cart when the product has a
         cover option, or when it sells only by grade (a Grade option and no
         quickship fabrics) -- otherwise "add to cart" would buy a bare
         grade with no fabric named */
      var needFabric = pd.dataset.hocPdNeedsFabric === 'true' ||
        (!!pd.querySelector('[data-hoc-pd-grade-values]') && !quickshipBtns.length);

      /* the mobile sticky add bar's own mirror elements -- see the top of
         this file for why they exist. Optional throughout: a section built
         before this bar existed, or a redeployed template that dropped it,
         still works fine with these simply absent. */
      var stickybar = pd.querySelector('[data-hoc-pd-stickybar]');
      var sbThumb = pd.querySelector('[data-hoc-pd-sb-thumb]');
      var sbPrice = pd.querySelector('[data-hoc-pd-sb-price]');
      var sbAtc = pd.querySelector('[data-hoc-pd-sb-atc]');
      var sbScroll = pd.querySelector('[data-hoc-pd-sb-scroll]');
      var figure = pd.querySelector('.hoc-pd__figure');

      /* The drawer's own [data-hoc-fabric-*] hooks (assets/hoc-base.js's
         bindPick) live inside this section too -- the fabric selector's
         name/chip, the price note beside it, the hero's material inset, and
         the hidden cart property. bindPick only ever fills them in; nothing
         clears them back when a shopper picks a Quickship Fabric button
         instead, so this page snapshots each one's pre-pick state once,
         here, before anything can touch it, and restores exactly that in
         resetFabricUI() below. Scoped to this section so a fabric selector
         belonging to a different product/section on the same page is never
         touched by this product's own reset. */
      var fabricNameEls = Array.prototype.slice.call(pd.querySelectorAll('[data-hoc-fabric-name]'));
      var fabricNoteEls = Array.prototype.slice.call(pd.querySelectorAll('[data-hoc-fabric-note]'));
      var fabricImageEls = Array.prototype.slice.call(pd.querySelectorAll('[data-hoc-fabric-image]'));
      var fabricInputEls = Array.prototype.slice.call(pd.querySelectorAll('[data-hoc-fabric-input]'));
      var fabricSwatchEls = Array.prototype.slice.call(pd.querySelectorAll('[data-hoc-fabric-swatch]'));
      /* made-to-order extras: the base (ivory) photo, its caption, the
         grade line on the swatch card, the lead-time line, and the hidden
         cart properties that let the cart show the same photo + swatch */
      var baseImg = stage ? stage.dataset.baseImg || '' : '';
      var baseImgSm = stage ? stage.dataset.baseImgSm || '' : '';
      var baseCart = stage ? stage.dataset.baseCart || '' : '';
      var baseCap = pd.querySelector('[data-hoc-pd-basecap]');
      var matGrade = pd.querySelector('[data-hoc-pd-matgrade]');
      var leadEl = pd.querySelector('[data-hoc-pd-lead]');
      var props = {};
      Array.prototype.slice.call(pd.querySelectorAll('[data-hoc-pd-prop]')).forEach(function (el) {
        props[el.dataset.hocPdProp] = el;
      });
      function setProp(key, val) {
        var el = props[key];
        if (!el) return;
        el.value = val || '';
        el.disabled = !val;
      }

      var fabricDefaults = {
        name: fabricNameEls.map(function (el) { return el.textContent; }),
        note: fabricNoteEls.map(function (el) { return el.textContent; }),
        image: fabricImageEls.map(function (el) { return el.style.backgroundImage; }),
        input: fabricInputEls.map(function (el) { return el.value; })
      };

      function resetFabricUI() {
        fabricNameEls.forEach(function (el, i) { el.textContent = fabricDefaults.name[i]; });
        fabricNoteEls.forEach(function (el, i) { el.textContent = fabricDefaults.note[i]; });
        fabricImageEls.forEach(function (el, i) { el.style.backgroundImage = fabricDefaults.image[i]; });
        fabricInputEls.forEach(function (el, i) { el.value = fabricDefaults.input[i]; });
        fabricSwatchEls.forEach(function (el) {
          el.hidden = true;
          var tile = el.querySelector('[data-hoc-fabric-swatch-img]');
          if (tile) tile.style.backgroundImage = '';
        });
        if (baseCap) baseCap.hidden = true;
        if (matGrade) matGrade.textContent = '';
        setProp('base', '');
        setProp('swatch', '');
      }
      /* o1/o2/o3 are the only slots the variant JSON ever carries (see the
         section's own comment above data-hoc-pd-variants) — anything else
         in the dataset falls back to o1, same as an option the section
         never found at all. */
      var validKeys = { o1: true, o2: true, o3: true };
      var gradeKey = validKeys[pd.dataset.hocPdGradeKey] ? pd.dataset.hocPdGradeKey : 'o1';
      var quickshipKey = validKeys[pd.dataset.hocPdQuickshipKey] ? pd.dataset.hocPdQuickshipKey : 'o1';

      var size = pd.dataset.hocPdSize || '';
      var grade = pd.dataset.hocPdGrade || '';
      var quickship = pd.dataset.hocPdQuickship || '';
      var fabric = null;

      function norm(s) { return (s || '').toString().trim().toLowerCase(); }

      /* Whole-word containment, case-insensitive: true when `word` appears
         in `haystack` as its own word, not as part of a longer one --
         "Raisin" matches inside "Billie Raisin" but "Sun" does not match
         inside "Sunday Oak". Used only for the drawer/quickship name match
         below, where the drawer's fuller fabric names and this product's
         own short Quickship Fabric labels are expected to overlap on a
         shared word rather than be identical strings. */
      function hasWord(haystack, word) {
        word = (word || '').toString().trim();
        if (!word) return false;
        var esc = word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        return new RegExp('\\b' + esc + '\\b', 'i').test((haystack || '').toString());
      }

      /* the file name of an image url, without its folder or query, so the
         same upload compares equal at any size */
      function fileOf(url) {
        var m = (url || '').toString().replace(/^url\(["']?|["']?\)$/g, '').split('?')[0].split('/').pop();
        return (m || '').toLowerCase();
      }
      /* a quickship button's own swatch image, before or after the lazy
         loader has moved it from data-hoc-bg to the inline style */
      function swatchOf(btn) {
        var el = btn.querySelector('.hoc-pd__qsswatch');
        if (!el) return '';
        return el.getAttribute('data-hoc-bg') || el.style.backgroundImage || '';
      }

      /* which variant slot holds the size: o2 on the original catalogue
         (cover / size), o1 on the restructured products (size / grade /
         quickship) -- found from the starting size's own value */
      var sizeKey = 'o2';
      ['o1', 'o2', 'o3'].some(function (k) {
        if (size && variants.some(function (v) { return norm(v[k]) === norm(size); })) { sizeKey = k; return true; }
        return false;
      });

      function bySize(list) {
        if (!size) return list;
        var hit = list.filter(function (v) { return norm(v[sizeKey]) === norm(size); });
        return hit.length ? hit : list;
      }

      function byGrade(list) {
        if (!grade || !gradeBtns.length) return list;
        var hit = list.filter(function (v) { return norm(v[gradeKey]) === norm(grade); });
        return hit.length ? hit : list;
      }

      function byQuickship(list) {
        if (!quickship || !quickshipBtns.length) return list;
        var hit = list.filter(function (v) { return norm(v[quickshipKey]) === norm(quickship); });
        return hit.length ? hit : list;
      }

      function announceGrade() {
        if (!gradeBtns.length) return;
        document.dispatchEvent(new CustomEvent('hoc:grade', { detail: { grade: grade } }));
      }

      /* married first, then the fabric's grade, then whatever is current —
         narrowed by size, by grade and by the quickship picker at every
         step, same as the drawer's own married/graded fallback */
      function resolve() {
        if (!variants.length) return null;
        if (fabric) {
          var married = bySize(byGrade(byQuickship(variants.filter(function (v) { return norm(v.o1) === norm(fabric.name); }))));
          if (married.length) return married[0];

          if (fabric.grade) {
            /* the fabric's grade ("B", "Grade B", "L") as the option value
               the catalogue uses ("Grade B", "Leather"), looked for in the
               cover slot (o1) and in the Grade option's own slot */
            var g = norm(fabric.grade).replace(/^grade\s*/, '');
            var wanted = [ 'grade ' + g ];
            if (g === 'l' || g === 'leather') wanted.push('leather');
            var graded = bySize(byGrade(byQuickship(variants.filter(function (v) {
              return wanted.indexOf(norm(v.o1)) > -1 || wanted.indexOf(norm(v[gradeKey])) > -1;
            }))));
            if (graded.length) {
              /* Prefer this grade's neutral "Special Order" placeholder
                 variant over an arbitrary real quickship fabric -- a
                 made-to-order pick should never silently show a specific
                 quickship name (e.g. "Raisin") in cart/checkout when the
                 shopper actually chose something else from the drawer. */
              var placeholder = graded.filter(function (v) {
                return /special order/i.test(v[quickshipKey] || '');
              });
              /* and of those, the one named for THIS grade ("Grade C -
                 Special Order Fabric", "Leather - Special Order Leather"),
                 so a product that still carries every grade x placeholder
                 combination never lands a grade C pick on the leather one */
              var own = placeholder.filter(function (v) {
                var q = norm(v[quickshipKey]);
                return wanted.some(function (w) { return q.indexOf(w) === 0; });
              });
              return own[0] || placeholder[0] || graded[0];
            }
          }
        }
        var keep = bySize(byGrade(byQuickship(variants)));
        return keep[0] || variants[0];
      }

      function pad(n) { return n < 10 ? '0' + n : String(n); }

      function paint(url, urlSm) {
        if (!stage || !url) return;
        /* urlSm (a mobile-width rendition) is optional. When it's present and
           the viewport is narrow, paint the smaller image instead -- keeps the
           hero responsive after a variant/thumbnail swap, not just on first
           load, since this is the one place that sets the hero's image. The
           identity checks below (t.dataset.img === url) still compare against
           the big url, which is what thumbs and the variant JSON both key on. */
        var chosen = (urlSm && window.matchMedia && window.matchMedia('(max-width: 749px)').matches) ? urlSm : url;
        var next = new Image();
        next.src = chosen;
        stage.classList.add('is-loading');
        window.setTimeout(function () {
          stage.style.backgroundImage = 'url(' + chosen + ')';
          stage.classList.remove('is-loading');
        }, 110);

        /* the mobile sticky bar's own thumbnail mirrors the exact same url
           the stage just painted -- same request, so the browser serves it
           from cache rather than fetching a second image just for a 42px
           tile */
        if (sbThumb) sbThumb.style.backgroundImage = 'url(' + chosen + ')';

        var at = 0;
        thumbs.forEach(function (t, i) {
          var on = t.dataset.img === url;
          t.setAttribute('aria-pressed', on ? 'true' : 'false');
          if (on) at = i + 1;
        });

        /* the caption is a plate number, so it counts the frames rather than
           the variants — a fabric with no photograph of its own leaves it
           where it was rather than pointing at nothing */
        if (figno && at) figno.textContent = 'fig. ' + pad(at) + ' / ' + pad(parseInt(figno.dataset.total || thumbs.length, 10));
      }

      function sync() {
        var v = resolve();
        if (!v) return;
        if (idInput) idInput.value = v.id;
        if (priceEl) priceEl.textContent = v.price;
        if (sbPrice) sbPrice.textContent = v.price;
        /* Made to order = a drawer fabric that did NOT land on a married
           variant of its own (a photographed, in-stock quickship cover).
           Made to order has no photo of its own on this frame, so the hero
           shows the piece in its neutral base cover and the swatch card
           says what it will be made in; a married or quickship fabric shows
           its own real photo, exactly as before. */
        var married = !!(fabric && norm(v.o1) === norm(fabric.name));
        var mto = !!(fabric && !married);
        /* a married fabric whose variant carries no photo of its own can
           still have one in the gallery under its name (California's
           ..._NevisLINEN_... with no image assigned to the variant) */
        var namedImg = null;
        if (married && !v.image && thumbs.length) {
          var key = norm(fabric.name).replace(/[^a-z0-9]/g, '');
          namedImg = thumbs.filter(function (t) {
            return key && (t.dataset.img || '').toLowerCase().replace(/[^a-z0-9]/g, '').indexOf(key) > -1;
          })[0] || null;
        }
        if (mto && baseImg) paint(baseImg, baseImgSm);
        else if (v.image) paint(v.image, v.imageSm);
        else if (namedImg) paint(namedImg.dataset.img, namedImg.dataset.imgSm);
        /* the swatch card is for made to order only: a married fabric's own
           photograph already shows it */
        if (married) fabricSwatchEls.forEach(function (el) { el.hidden = true; });

        if (baseCap) baseCap.hidden = !(mto && baseImg);
        if (matGrade) matGrade.textContent = (fabric && fabric.grade) ? 'grade ' + String(fabric.grade).replace(/^grade\s*/i, '').toLowerCase() : '';
        setProp('base', mto ? baseCart : '');
        setProp('swatch', mto ? (fabric.image || '') : '');

        var lead = '';
        if (leadEl) {
          if (mto) lead = leadEl.dataset.mto || '';
          else if (married || (quickship && quickshipBtns.length)) lead = leadEl.dataset.qs || '';
          leadEl.textContent = lead;
          leadEl.hidden = !lead;
        }
        setProp('ships', lead);
        if (atc) {
          /* Only sold out disables the button. Before a fabric is chosen it
             stays full strength and says "choose a fabric" -- a faded button
             read as broken -- and a click opens the drawer (below) instead of
             adding to the cart. */
          atc.disabled = v.available === false;
          if (v.available === false) { atc.textContent = atc.dataset.soldout || 'sold out'; }
          else if (needFabric && !fabric) { atc.textContent = atc.dataset.needs || 'choose a fabric'; }
          else { atc.textContent = atc.dataset.label || 'add to cart'; }
          /* the sticky bar's own button mirrors the real one's disabled
             state and label exactly -- it never decides either on its own */
          if (sbAtc) {
            sbAtc.disabled = atc.disabled;
            sbAtc.textContent = atc.textContent;
          }
        }
      }

      sizeBtns.forEach(function (btn) {
        btn.addEventListener('click', function () {
          size = btn.dataset.hocPdSize || '';
          sizeBtns.forEach(function (b) { b.setAttribute('aria-pressed', b === btn ? 'true' : 'false'); });
          sync();
        });
      });

      /* the single path every real grade change runs through, whether it
         came from a click on one of this page's own buttons or from a
         hoc:grade:request dispatched by something else asking for the same
         change */
      function applyGrade(nextGrade) {
        if (!gradeBtns.length) return;
        grade = nextGrade || '';
        gradeBtns.forEach(function (b) {
          b.setAttribute('aria-pressed', norm(b.dataset.hocPdGrade) === norm(grade) ? 'true' : 'false');
        });
        sync();
        announceGrade();
      }

      gradeBtns.forEach(function (btn) {
        btn.addEventListener('click', function () { applyGrade(btn.dataset.hocPdGrade || ''); });
      });

      /* lets something outside this script's own buttons drive the exact
         same grade-change path a real click here would. Nothing in this
         theme dispatches this today -- the fabric drawer's grade row is an
         independent filter now, not a control synced to this picker -- but
         the path stays available for anything added later that should move
         the real grade the way a click would. */
      document.addEventListener('hoc:grade:request', function (e) {
        applyGrade((e.detail && e.detail.grade) || '');
      });

      /* the same single path for quickship fabric, so a click on one of
         this page's own buttons and a request dispatched from elsewhere
         both go through state, aria-pressed and sync exactly once */
      function applyQuickship(nextQuickship) {
        if (!quickshipBtns.length) return;
        quickship = nextQuickship || '';
        quickshipBtns.forEach(function (b) {
          b.setAttribute('aria-pressed', norm(b.dataset.hocPdQuickship) === norm(quickship) ? 'true' : 'false');
        });

        /* A quickship fabric pick and a drawer fabric pick are mutually
           exclusive choices, the same way a drawer pick already clears a
           stale quickship selection below (see the hoc:fabric handler) --
           picking a real in-stock quickship fabric means any fabric chosen
           from the drawer no longer applies. Clear the closure variable so
           resolve() stops trying to marry/grade against it, and put the
           drawer's own [data-hoc-fabric-*] hooks back the way they were
           before that pick -- the name, the price note, the fabric chip and
           material inset, and the hidden cart property -- so none of it is
           left showing (or riding along to the cart) once the shopper has
           moved on to a quickship fabric instead. */
        if (quickship && fabric) {
          fabric = null;
          resetFabricUI();
        }

        sync();
      }

      quickshipBtns.forEach(function (btn) {
        btn.addEventListener('click', function () { applyQuickship(btn.dataset.hocPdQuickship || ''); });
      });

      document.addEventListener('hoc:quickship:request', function (e) {
        applyQuickship((e.detail && e.detail.quickship) || '');
      });

      if (atc) {
        atc.addEventListener('click', function (e) {
          if (!needFabric || fabric) return;
          e.preventDefault();
          /* hand off to the fabric selector, which the drawer is already
             listening to -- this page never needs to know which drawer */
          var opener = pd.querySelector('[data-hoc-dw-open]') || document.querySelector('[data-hoc-dw-open]');
          if (opener) opener.click();
        });
      }

      thumbs.forEach(function (t) {
        t.addEventListener('click', function () { paint(t.dataset.img, t.dataset.imgSm); });
      });

      /* the sticky bar's own add-to-cart button never submits the form or
         decides sold-out/needs-fabric for itself -- it only proxies a click
         to the real button above, so every rule atc's own listeners already
         enforce (this one included) applies here for free */
      if (sbAtc && atc) {
        sbAtc.addEventListener('click', function () { atc.click(); });
      }

      /* tapping the sticky bar's photo/name is the fastest way back to the
         actual photo it is standing in for */
      if (sbScroll && figure) {
        sbScroll.addEventListener('click', function () {
          figure.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
      }

      /* shows the sticky bar once the real hero photo has scrolled fully
         out of view, and hides it again once it's back -- the one thing
         this bar exists to react to. display:none above 1000px (see
         assets/hoc-product.css) makes this a no-op on desktop regardless of
         what the observer reports, so there's no separate breakpoint check
         needed here. */
      if (stickybar && stage && 'IntersectionObserver' in window) {
        var stickybarIO = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            stickybar.classList.toggle('is-visible', !entry.isIntersecting);
          });
        });
        stickybarIO.observe(stage);
      }

      /* the drawer announces; this page decides what it means */
      document.addEventListener('hoc:fabric', function (e) {
        var picked = e.detail || null;

        /* A fabric picked from the drawer can happen to be one of this
           product's own in-stock Quickship Fabric values (e.g. "Raisin") --
           the drawer has no idea the two lists overlap, since it only
           knows its own open-ended fabric library, under its own fuller
           names ("Billie Raisin" for the "Raisin" quickship button). When
           the quickship value shows up as a whole word in the drawer pick's
           name, treat the pick exactly like a click on that quickship
           button instead of a made-to-order fabric: the real photographed
           variant and its own image swap in, not the small material-inset
           swatch this page shows for a fabric with no variant of its own.
           bindPick already wrote this pick into the [data-hoc-fabric-*]
           hooks before announcing it, so undo that first -- a real click
           handles state, aria-pressed and sync from here exactly as it
           would on its own.

           Whole-word rather than exact-string, so a shortened button label
           still finds its longer drawer counterpart; this is a judgment
           call the catalogue's naming can call for revisiting if it ever
           produces an unwanted match. */
        if (picked && quickshipBtns.length) {
          /* Same fabric = same swatch file, when the quickship value has a
             swatch image of its own: a colour word alone is not enough
             ("Askew Ivory" and "Mex Cream" are not the quickship Ivory or
             Cream). Only a quickship value with no swatch image falls back
             to the whole-word name match. */
          var pickedFile = fileOf(picked.image);
          var qsMatch = quickshipBtns.filter(function (b) {
            var f = fileOf(swatchOf(b));
            if (f) return !!pickedFile && f === pickedFile;
            return hasWord(picked.name, b.dataset.hocPdQuickship || '');
          })[0];
          if (qsMatch) {
            resetFabricUI();
            fabric = null;
            qsMatch.click();
            return;
          }
        }

        fabric = picked;


        /* A made-to-order fabric pick and a quickship fabric pick are
           mutually exclusive choices -- picking a real fabric from the
           drawer means any stale quickship selection no longer applies to
           what the shopper just chose. Clear it the same way
           applyQuickship() resets pressed-state, and do it before resolving
           the variant below, so byQuickship() is a no-op (its own guard
           already treats a falsy quickship as "don't filter") and the
           grade-only match isn't accidentally constrained by whatever
           quickship fabric happened to be pressed before. */
        if (fabric && quickshipBtns.length) {
          quickship = '';
          quickshipBtns.forEach(function (b) {
            b.setAttribute('aria-pressed', 'false');
          });
        }

        /* The drawer's grade row is now an independent filter, not a synced
           control (a shopper can pick a fabric of any grade regardless of
           what is pressed here), so a pick can silently resolve to a variant
           of a different grade than this page currently shows pressed.
           Resolve once, read the grade the picked variant actually carries,
           and if it differs, bring this picker's own pressed state and
           closure variable in line with it -- the same pressed-state update
           applyGrade() makes on a real click, minus the hoc:grade:request
           round trip, which would just be this page asking itself for a
           change it is already making. */
        if (gradeBtns.length) {
          var v = resolve();
          if (v) {
            var actual = v[gradeKey] || '';
            if (norm(actual) !== norm(grade)) {
              grade = actual;
              gradeBtns.forEach(function (b) {
                b.setAttribute('aria-pressed', norm(b.dataset.hocPdGrade) === norm(grade) ? 'true' : 'false');
              });
              announceGrade();
            }
          }
        }

        sync();
      });

      sync();
      /* announce the default grade (the first variant's) once, for anything
         else on the page that wants to know it on the very first paint */
      announceGrade();
    });
  }

  function init(root) { initProduct(root || document); }

  document.addEventListener('DOMContentLoaded', function () { init(document); });
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
